// src/pages/accounting/salaryCostHelpers.js
// ─────────────────────────────────────────────────────────────────────────
// Pure helpers for the Salary Cost page. Kept dependency-free so the salary
// math can be unit-tested without pulling in the (Supabase-bound) page module.
// ─────────────────────────────────────────────────────────────────────────

// Standard full-time monthly hours used across the app to turn a monthly
// salary into an hourly rate (salary ÷ 176) and back. Matches the divisor
// used in getInternalCostRate / the salary-ledger deriver.
export const STANDARD_FTE_HOURS = 176;

/**
 * Pro-rate a full-time-equivalent monthly amount (salary or bonus) to the
 * hours actually worked/approved in the month.
 *
 * Salaried staff cost a fixed monthly salary; a freelancer is paid per hour,
 * so their monthly cost is the FTE rate scaled by `hours / 176`. A full-time
 * month (176 h) is returned unchanged; a month above 176 h scales above the
 * base (e.g. an overtime freelancer month).
 *
 * Guardrails: a null/undefined amount passes through unchanged, and a
 * missing / non-positive hours value returns the amount untouched (the caller
 * decides *whether* to pro-rate — this only does the arithmetic when asked).
 *
 * @param {number|null|undefined} amount FTE monthly amount (INR or EUR)
 * @param {number|null|undefined} hours  approved/contracted hours for the month
 * @returns {number|null|undefined}
 */
export const prorateToHours = (amount, hours) => {
  if (amount == null) return amount;
  if (hours == null || hours <= 0) return amount;
  return amount * (hours / STANDARD_FTE_HOURS);
};

/**
 * Decide whether a resolved salary amount is a FULL-TIME-EQUIVALENT figure
 * that is safe to pro-rate to a freelancer's actual hours.
 *
 * Pro-ratable (the amount is an FTE rate, not yet scaled to hours):
 *   - 'salary_matrix_nl' / 'salary_matrix' — CAO / IN matrix lookups (FTE)
 *   - 'derived' for an IN user — the ledger deriver stores the full IN-matrix
 *     salary for IN (it does NOT divide by contracted hours)
 *
 * NOT pro-ratable (leave untouched):
 *   - 'legacy' — imported actual monthly pay. For an hourly freelancer this
 *     already equals hours × rate; pro-rating would double-count.
 *   - 'override' / 'individual_override' — admin-entered actual amounts.
 *   - 'derived' for an NL user — NL derived rows are ALREADY pro-rated by the
 *     deriver (matrix × contracted_hours / 176), so never re-pro-rate them.
 *   - anything else ('user_base', 'missing', 'none', '…') — unknown/loading.
 *
 * @param {string|null|undefined} source salary source tag from resolveSalary
 * @param {string|null|undefined} office user office_location ('NL' | 'IN')
 * @returns {boolean}
 */
export const isFteSalarySource = (source, office) => {
  if (source === 'salary_matrix_nl' || source === 'salary_matrix') return true;
  if (source === 'derived' && office === 'IN') return true;
  return false;
};
